/**
 * Theme controller for the web app (ARCHITECTURE.md §8 — dark-mode, *selected*).
 *
 * `@richrich/ui`'s `detectTheme`/`useResolvedTheme` only *read* the document; the
 * web app owns *writing* the `.dark`/`.light` class on `<html>` and persisting the
 * choice. A blocking inline script in `index.html` applies the stored value before
 * first paint; this module keeps it in sync at runtime. Default is light.
 */
import { detectTheme, type Theme } from "@richrich/ui";

const STORAGE_KEY = "rr-theme";

/** Read the persisted preference; defaults to `'light'` when unset. */
export function storedTheme(): Theme {
  if (typeof localStorage === "undefined") return "light";
  const v = localStorage.getItem(STORAGE_KEY);
  return v === "dark" ? "dark" : "light";
}

/** Apply and persist a theme, updating the `<html>` class. */
export function setTheme(theme: Theme): void {
  const el = document.documentElement;
  el.classList.remove("light", "dark");
  el.classList.add(theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Storage may be unavailable (private mode); the class still applies.
  }
}

/** Flip between light and dark, returning the new value. */
export function toggleTheme(): Theme {
  const next: Theme = detectTheme() === "dark" ? "light" : "dark";
  setTheme(next);
  return next;
}

/** Ensure the DOM reflects the persisted preference (idempotent). */
export function initTheme(): void {
  setTheme(storedTheme());
}
